import { useState } from "react";
import { CalculationOutput } from "../types";
import { PeriodMode, fmtEUR, suffix } from "../utils/period";

export default function FinancialSummary({
  output,
  periodMode,
  isPremium = true,
}: {
  output: CalculationOutput | null;
  periodMode: PeriodMode;
  isPremium?: boolean;
}) {
  if (!output) return null;
  const [summaryMode, setSummaryMode] = useState<"cash" | "fiscal">("cash");

  const fmtByPeriod = (annual: number) => `${fmtEUR(annual, periodMode)}${suffix(periodMode)}`;
  const fmtPct = (v: number) => `${(v || 0).toFixed(1)}%`;

  const bruto = (output as any).brutoFacturado ?? 0;
  const rendNeto = output.rendimientoNeto ?? 0;
  const irpfTotal = (output as any).irpfTotal ?? output.irpf?.cuotaTotal ?? 0;
  const netoFiscal = output.netoFiscalAnual ?? rendNeto - irpfTotal;
  const caja = (output as any).cajaPreviaRenta ?? 0;
  const netoFinal = (output as any).netoFinalTrasRenta ?? netoFiscal;
  const gastosYReta =
    ((output as any).costesCash ?? 0) + ((output as any).cuotaRETAAnual ?? 0);
  const anticipos = (output as any).anticiposIRPF ?? 0;
  const irpfRegularizacion = (output as any).irpfRegularizacion ?? (irpfTotal - anticipos);
  const pctBruto =
    bruto > 0 ? (output.irpfPctSobreBruto ?? (irpfTotal / bruto) * 100) : 0;

  const cards = isPremium
    ? getSummaryCards({
        mode: summaryMode,
        fmtByPeriod,
        fmtPct,
        values: {
          bruto,
          rendNeto,
          irpfTotal,
          anticipos,
          irpfRegularizacion,
          caja,
          netoFinal,
          netoFiscal,
          pctBruto,
        },
      })
    : getLiteSummaryCards({
        fmtByPeriod,
        values: {
          bruto,
          rendNeto,
          netoFiscal,
          irpfTotal,
        },
      });

  return (
    <div>
      {isPremium && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>Vista</div>
          <div style={{ display: "inline-flex", background: "#f3f4f6", borderRadius: "10px", padding: "4px" }}>
            {(["cash", "fiscal"] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setSummaryMode(mode)}
                style={{
                  appearance: "none",
                  border: "none",
                  background: summaryMode === mode ? "#fff" : "transparent",
                  color: summaryMode === mode ? "var(--text)" : "var(--muted)",
                  fontWeight: 600,
                  fontSize: "13px",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: summaryMode === mode ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                  transition: "all 0.15s ease",
                }}
              >
                {mode === "cash" ? "Caja" : "Fiscal"}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
        {cards.map((card) => (
          <MetricCard
            key={card.label}
            tone={card.tone}
            label={card.label}
            value={card.value}
            sub={card.sub}
            tooltip={card.tooltip}
          />
        ))}
      </div>

      {!isPremium && (
        <div
          style={{
            fontSize: "11px",
            color: "var(--muted)",
            textAlign: "center",
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #fde68a",
          }}
        >
          Versión simplificada. Accede a Premium para ver detalle de IRPF, IVA, trimestres y comparación de escenarios.
        </div>
      )}

      <div
        style={{
          fontSize: "10px",
          color: "var(--muted)",
          textAlign: "center",
          marginTop: "10px",
        }}
      >
        Cifras calculadas automáticamente según tus datos y comunidad autónoma
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  sub,
  tooltip,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tooltip?: string;
  tone?: "primary";
}) {
  const isPrimary = tone === "primary";
  return (
    <div
      title={tooltip}
      style={{
        backgroundColor: isPrimary ? "#f0f4ff" : "#fff",
        border: isPrimary ? "1px solid #c7d2fe" : "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        padding: "16px",
        boxShadow: isPrimary ? "var(--shadow)" : "var(--shadow)",
      }}
    >
      <div style={{ fontSize: "11px", fontWeight: 600, color: isPrimary ? "#6366f1" : "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>
        {label}
      </div>
      <div style={{ fontSize: isPrimary ? "32px" : "20px", fontWeight: 800, color: isPrimary ? "#312e81" : "var(--text)", letterSpacing: "-0.02em", marginBottom: "4px" }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: "11px", color: isPrimary ? "#6366f1" : "var(--muted)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function getSummaryCards({
  mode,
  fmtByPeriod,
  fmtPct,
  values,
}: {
  mode: "cash" | "fiscal";
  fmtByPeriod: (n: number) => string;
  fmtPct: (n: number) => string;
  values: {
    bruto: number;
    rendNeto: number;
    irpfTotal: number;
    anticipos: number;
    irpfRegularizacion: number;
    caja: number;
    netoFinal: number;
    netoFiscal: number;
    pctBruto: number;
  };
}) {
  if (mode === "cash") {
    return [
      {
        label: "Caja neta estimada",
        value: fmtByPeriod(values.netoFinal),
        sub: "Caja tras regularización IRPF (pago/devolución).",
        tone: "primary" as const,
      },
      {
        label: "Caja antes de Renta",
        value: fmtByPeriod(values.caja),
        sub: "Tras anticipos (retenciones + modelo 130), antes de la declaración.",
      },
      {
        label: "Anticipos IRPF (ret + 130)",
        value: fmtByPeriod(values.anticipos),
        sub: "Pagos adelantados durante el año.",
      },
      {
        label: "Regularización Renta",
        value: fmtByPeriod(values.irpfRegularizacion),
        sub: "Positivo: pagarías. Negativo: devolución.",
      },
    ];
  }

  return [
    {
      label: "Neto fiscal (tras IRPF estimado)",
      value: fmtByPeriod(values.netoFiscal),
      sub: "Rendimiento neto − IRPF total estimado del año.",
      tone: "primary" as const,
      },
    {
      label: "Rendimiento neto (antes IRPF)",
      value: fmtByPeriod(values.rendNeto),
      sub: "Bruto − gastos deducibles − cuota de autónomos.",
    },
    {
      label: "IRPF total estimado",
      value: fmtByPeriod(values.irpfTotal),
      sub: "Cuota total (estatal + autonómica) tras mínimos y deducciones.",
    },
    {
      label: "Tipo efectivo IRPF (sobre bruto)",
      value: values.bruto > 0 ? fmtPct(values.pctBruto) : "—",
      sub: "Porcentaje medio. No es el tipo marginal.",
    },
    {
      label: "Bruto facturado (sin IVA)",
      value: fmtByPeriod(values.bruto),
      sub: "Suma de bases imponibles de tus ingresos (sin IVA).",
    },
  ];
}

function getLiteSummaryCards({
  fmtByPeriod,
  values,
}: {
  fmtByPeriod: (n: number) => string;
  values: {
    bruto: number;
    netoFiscal: number;
    irpfTotal: number;
    rendNeto: number;
  };
}) {
  return [
    {
      label: "Bruto facturado (sin IVA)",
      value: fmtByPeriod(values.bruto),
      sub: "Total ingresos base imponible",
      tone: "primary" as const,
    },
    {
      label: "Rendimiento neto (antes IRPF)",
      value: fmtByPeriod(values.rendNeto),
      sub: "Bruto − gastos deducibles − cuota de autónomos.",
    },
    {
      label: "IRPF total estimado",
      value: fmtByPeriod(values.irpfTotal),
      sub: "Cuota total (estatal + autonómica) tras mínimos y deducciones.",
    },
    {
      label: "Neto fiscal (tras IRPF)",
      value: fmtByPeriod(values.netoFiscal),
      sub: "Rendimiento neto − IRPF total estimado del año.",
    },
  ];
}
